import * as React from 'react';
import { Check, Search, X } from 'lucide-react';
import type { VagaMapa } from '@/lib/tipos';
import { cn, numero } from '@/lib/utils';

/**
 * Busca de SKU DENTRO da rua aberta.
 *
 * POR QUE LOCAL, e não uma consulta
 * ---------------------------------
 * A busca do topo procura no CD inteiro e volta ao servidor. Aqui a pergunta
 * é outra e bem menor: "este item está nesta rua?". E a resposta já está na
 * mão — o mapa do corredor carrega cada vaga com os produtos e as quantidades
 * dela. Filtrar em memória responde no susto, a cada tecla, sem rede.
 *
 * Isso também muda o que dá para dizer. Uma consulta que não acha devolve
 * silêncio; aqui dá para afirmar **"não está nesta rua"**, que é uma resposta
 * de verdade para quem está com o coletor na mão em frente ao porta-palete.
 *
 * O CAMPO ACEITA CÓDIGO OU NOME
 * -----------------------------
 * Quem tem a etiqueta na mão digita o código; quem está olhando a mercadoria
 * digita "brastemp". Os dois chegam no mesmo lugar. Com um código exato que
 * existe na rua, a escolha é imediata — não faz sentido pedir mais um clique
 * numa lista de um item só.
 *
 * O resultado alimenta o realce que o rack já sabia fazer: as vagas com o
 * produto acendem, o resto apaga, e cada vaga acesa mostra a QUANTIDADE
 * daquele SKU ali — não o saldo total dela.
 */

interface Achado {
  sku: number;
  descricao: string;
  vagas: number;
  unidades: number;
}

export function BuscaNaRua({
  vagas,
  sku,
  aoEscolher,
}: {
  vagas: readonly VagaMapa[];
  sku: number | null;
  aoEscolher: (sku: number | null) => void;
}): JSX.Element {
  const [termo, definirTermo] = React.useState('');
  const [aberto, definirAberto] = React.useState(false);
  const caixa = React.useRef<HTMLDivElement>(null);

  /** Cada SKU da rua uma vez só, com em quantas vagas está e quanto tem. */
  const catalogo = React.useMemo(() => {
    const mapa = new Map<number, Achado>();
    for (const v of vagas) {
      for (const p of v.produtos) {
        if (p.quantidade <= 0) continue;
        const atual = mapa.get(p.sku);
        if (atual) {
          atual.vagas += 1;
          atual.unidades += p.quantidade;
        } else {
          mapa.set(p.sku, {
            sku: p.sku,
            descricao: p.descricao,
            vagas: 1,
            unidades: p.quantidade,
          });
        }
      }
    }
    return [...mapa.values()];
  }, [vagas]);

  const achados = React.useMemo(() => {
    const t = termo.trim().toLowerCase();
    if (t.length < 2) return [];
    return catalogo
      .filter((a) => String(a.sku).includes(t) || a.descricao.toLowerCase().includes(t))
      .sort((a, b) => b.unidades - a.unidades)
      .slice(0, 12);
  }, [catalogo, termo]);

  /*
   * "Não está nesta rua" é resposta, não erro.
   *
   * Só vale para termo numérico: um nome que não casa pode ser só grafia
   * diferente da descrição do cadastro, e afirmar ausência aí seria mentira.
   * Um código, não — ou o número está entre os SKUs da rua, ou não está.
   */
  const ausente =
    termo.trim().length >= 2 && achados.length === 0 && /^\d+$/.test(termo.trim());

  React.useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent): void => {
      if (caixa.current && !caixa.current.contains(e.target as Node)) definirAberto(false);
    };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);

  const escolhido = sku === null ? null : catalogo.find((a) => a.sku === sku) ?? null;

  if (sku !== null) {
    return (
      <span className="flex min-w-0 items-center gap-1.5 rounded-lg border border-primaria/40 bg-primaria-suave px-2 py-1 text-[12px]">
        <Check className="h-3.5 w-3.5 shrink-0 text-primaria" />
        <span className="tabular shrink-0 font-mono font-bold">{sku}</span>
        {escolhido ? (
          <span className="shrink-0 text-textoFraco">
            {escolhido.vagas} {escolhido.vagas === 1 ? 'vaga' : 'vagas'} ·{' '}
            {numero(escolhido.unidades)} un
          </span>
        ) : (
          <span className="shrink-0 text-textoFraco">não está nesta rua</span>
        )}
        <button
          type="button"
          onClick={() => {
            aoEscolher(null);
            definirTermo('');
          }}
          aria-label="Limpar busca de SKU"
          className="shrink-0 text-textoFraco hover:text-texto"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </span>
    );
  }

  return (
    <div ref={caixa} className="relative w-[190px] shrink-0">
      <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-textoFraco" />
      <input
        value={termo}
        onChange={(e) => {
          definirTermo(e.target.value);
          definirAberto(true);
        }}
        onFocus={() => definirAberto(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            // Um resultado só: entra direto. Com vários, a lista decide.
            const unico = achados[0];
            if (achados.length === 1 && unico) {
              aoEscolher(unico.sku);
              definirAberto(false);
            }
          } else if (e.key === 'Escape') {
            definirAberto(false);
          }
        }}
        placeholder="SKU nesta rua"
        aria-label="Procurar SKU nesta rua"
        autoComplete="off"
        className="h-[30px] w-full rounded-lg border border-borda bg-superficie pl-7 pr-2 text-[12px] outline-none focus:border-primaria focus:ring-2 focus:ring-primaria/25"
      />

      {aberto && termo.trim().length >= 2 ? (
        <div className="absolute left-0 z-40 mt-1 w-[280px] overflow-hidden rounded-lg border border-borda bg-superficie shadow-forte">
          {ausente ? (
            <p className="px-3 py-2 text-[12px]">
              <b>SKU {termo.trim()} não está nesta rua.</b>
              <span className="mt-0.5 block text-textoFraco">
                Use a busca do topo para achar em que rua ele está.
              </span>
            </p>
          ) : achados.length === 0 ? (
            <p className="px-3 py-2 text-[12px] text-textoFraco">
              Nenhum produto desta rua com esse texto.
            </p>
          ) : (
            <ul className="max-h-[260px] overflow-y-auto">
              {achados.map((a) => (
                <li key={a.sku}>
                  <button
                    type="button"
                    onClick={() => {
                      aoEscolher(a.sku);
                      definirAberto(false);
                    }}
                    className={cn(
                      'flex w-full flex-col items-start gap-0.5 px-3 py-1.5 text-left hover:bg-superficie2',
                    )}
                  >
                    <span className="flex w-full items-baseline gap-2">
                      <span className="tabular shrink-0 font-mono text-[12px] font-bold">
                        {a.sku}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-[12px]">{a.descricao}</span>
                    </span>
                    <span className="tabular text-[11px] text-textoFraco">
                      {a.vagas} {a.vagas === 1 ? 'vaga' : 'vagas'} · {numero(a.unidades)} un
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
